/**
 * ZoomController - Chart Zoom Functionality Service
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: This service is solely responsible for managing chart zoom/pan
 * functionality. It handles zoom state, mouse interactions, and data filtering
 * without any knowledge of chart rendering or data processing concerns.
 * 
 * Responsibilities:
 * - Track zoom interaction state (dragging, selection area)
 * - Handle mouse events for zoom selection
 * - Filter chart data by time ranges
 * - Manage zoom history for undo functionality
 * - Calculate appropriate zoom levels and limits
 * 
 * Context: Users need to zoom into specific time periods to examine sensor
 * data in detail. The zoom function selects a time range by dragging on the
 * chart and filters data to show only that period.
 */

export class ZoomController {
  constructor() {
    this.zoomState = {
      left: '',           // Start time of selection
      right: '',          // End time of selection  
      dragging: false,    // Whether user is currently dragging
      zoomHistory: []     // History of zoom operations for undo
    };
    
    this.originalData = []; // Reference to unfiltered data
    this.onZoomChange = null; // Callback for zoom state changes
    this.onDragStateChange = null; // Callback for drag state changes (for real-time updates)
  }

  /**
   * Sets the original unfiltered dataset
   * 
   * @param {Array} data - Complete chart data
   */
  setOriginalData(data) {
    this.originalData = [...data];
  }

  /**
   * Sets callback function to notify when zoom state changes
   * 
   * @param {Function} callback - Function to call on zoom change
   */
  setZoomChangeCallback(callback) {
    this.onZoomChange = callback;
  }

  /**
   * Sets callback function to notify when drag state changes (for real-time updates)
   * 
   * @param {Function} callback - Function to call on drag state change
   */
  setDragStateChangeCallback(callback) {
    this.onDragStateChange = callback;
  }

  /**
   * Handles mouse down event to start zoom selection
   * 
   * @param {Object} event - Chart mouse event
   * @param {string} event.activeLabel - Timestamp at mouse position
   */
  handleMouseDown(event) {
    if (event?.activeLabel) {
      this._updateZoomState({
        left: event.activeLabel,
        right: '',
        dragging: true
      });
    }
  }

  /**
   * Handles mouse move event during zoom selection
   * 
   * @param {Object} event - Chart mouse event
   * @param {string} event.activeLabel - Timestamp at current mouse position
   */
  handleMouseMove(event) {
    if (this.zoomState.dragging && event?.activeLabel) {
      this._updateZoomState({
        ...this.zoomState,
        right: event.activeLabel
      });
    }
  }

  /**
   * Handles mouse up event to complete zoom selection
   * 
   * Validates the selection and applies zoom if valid, otherwise cancels.
   */
  handleMouseUp() {
    const { left, right, dragging } = this.zoomState;
    
    if (!dragging || !left || !right || left === right) {
      this._cancelZoomSelection();
      return;
    }

    this._applyZoom(left, right);
  }

  /**
   * Resets zoom to show all original data
   */
  resetZoom() {
    // Add current zoom to history before resetting
    if (this._isZoomed()) {
      this.zoomState.zoomHistory.push({
        type: 'reset',
        timestamp: new Date().toISOString()
      });
    }

    this._updateZoomState({
      left: '',
      right: '',
      dragging: false
    });

    // Notify with original data
    if (this.onZoomChange) {
      this.onZoomChange({
        data: this.originalData,
        isZoomed: false,
        zoomLevel: this._calculateZoomLevel(this.originalData.length, this.originalData.length)
      });
    }
  }

  /**
   * Applies zoom to specific time range
   * 
   * @param {string|Date} startTime - Start of zoom range
   * @param {string|Date} endTime - End of zoom range
   * @private
   */
  _applyZoom(startTime, endTime) {
    console.log('🔍 _applyZoom called with:', { startTime, endTime });
    console.log('🔍 originalData length:', this.originalData.length);
    
    // Ensure proper time ordering
    const leftTime = new Date(startTime < endTime ? startTime : endTime);
    const rightTime = new Date(startTime < endTime ? endTime : startTime);
    
    console.log('🔍 Time range:', { leftTime, rightTime });
    
    // Filter data to zoom range
    const filteredData = this._filterDataByTimeRange(
      this.originalData, 
      leftTime, 
      rightTime
    );
    
    console.log('🔍 Filtered data length:', filteredData.length);
    console.log('🔍 First few filtered points:', filteredData.slice(0, 3));
    
    if (filteredData.length > 0) {
      // Save zoom operation to history
      this.zoomState.zoomHistory.push({
        type: 'zoom',
        startTime: leftTime.toISOString(),
        endTime: rightTime.toISOString(),
        timestamp: new Date().toISOString(),
        dataPoints: filteredData.length
      });
      
      console.log('🔍 Calling onZoomChange callback with filtered data');
      // Notify listeners of zoom change
      if (this.onZoomChange) {
        this.onZoomChange({
          data: filteredData,
          isZoomed: true,
          zoomLevel: this._calculateZoomLevel(filteredData.length, this.originalData.length),
          timeRange: {
            start: leftTime.toISOString(),
            end: rightTime.toISOString()
          }
        });
      } else {
        console.log('🔍 ⚠️ No onZoomChange callback set!');
      }
    } else {
      console.log('🔍 ⚠️ Filtered data too small, not applying zoom');
      this._clearZoomSelection();
      return;
    }
    
    // Keep the selection visible briefly after zoom, then clear
    setTimeout(() => {
      this._clearZoomSelection();
    }, 200);
  }

  /**
   * Filters chart data by time range
   * 
   * @param {Array} data - Chart data to filter
   * @param {Date} startTime - Range start
   * @param {Date} endTime - Range end
   * @returns {Array} Filtered data
   * @private
   */
  _filterDataByTimeRange(data, startTime, endTime) {
    const startTimestamp = startTime.getTime();
    const endTimestamp = endTime.getTime();
    
    return data.filter(item => {
      const itemTimestamp = new Date(item.time).getTime();
      return itemTimestamp >= startTimestamp && itemTimestamp <= endTimestamp;
    });
  }

  /**
   * Cancels current zoom selection
   * @private
   */
  _cancelZoomSelection() {
    this._updateZoomState({
      left: '',
      right: '',
      dragging: false
    });
  }

  /**
   * Clears zoom selection state
   * @private
   */
  _clearZoomSelection() {
    this._updateZoomState({
      left: '',
      right: '',
      dragging: false
    });
  }

  /**
   * Updates zoom state and triggers callbacks
   * 
   * @param {Object} newState - New state values
   * @private
   */
  _updateZoomState(newState) {
    console.log('🔄 _updateZoomState called with:', newState);
    this.zoomState = { ...this.zoomState, ...newState };
    console.log('🔄 Updated zoomState:', this.zoomState);
    
    // Notify React component of drag state changes for real-time updates
    if (this.onDragStateChange) {
      const { left, right, dragging } = this.zoomState;
      this.onDragStateChange({ left, right, dragging });
    }
  }

  /**
   * Checks if chart is currently zoomed
   * 
   * @returns {boolean} True if zoomed in
   * @private
   */
  _isZoomed() {
    return this.zoomState.zoomHistory.some(h => h.type === 'zoom');
  }

  /**
   * Calculates current zoom level as percentage
   * 
   * @param {number} currentDataPoints - Points in current view
   * @param {number} totalDataPoints - Points in original dataset
   * @returns {number} Zoom level (0-100, where 100 = fully zoomed out)
   * @private
   */
  _calculateZoomLevel(currentDataPoints, totalDataPoints) {
    if (totalDataPoints === 0) return 100;
    return Math.round((currentDataPoints / totalDataPoints) * 100);
  }

  /**
   * Gets current zoom state for rendering zoom selection overlay
   * 
   * @returns {Object} Current zoom state
   */
  getZoomState() {
    return { ...this.zoomState };
  }

  /**
   * Gets zoom history for debugging or undo functionality
   * 
   * @returns {Array} Array of zoom operations
   */
  getZoomHistory() {
    return [...this.zoomState.zoomHistory];
  }

  /**
   * Validates if a time range is appropriate for zooming
   * 
   * @param {string|Date} startTime - Range start
   * @param {string|Date} endTime - Range end
   * @returns {Object} Validation result with isValid flag and reason
   */
  validateZoomRange(startTime, endTime) {
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return { isValid: false, reason: 'Invalid timestamp format' };
      }
      
      if (start.getTime() === end.getTime()) {
        return { isValid: false, reason: 'Start and end times are identical' };
      }
      
      return { isValid: true };
      
    } catch (error) {
      return { isValid: false, reason: `Validation error: ${error.message}` };
    }
  }

  /**
   * Gets recommended zoom level for optimal data visualization
   * 
   * @param {number} dataPoints - Number of data points to display
   * @returns {string} Recommendation ('optimal', 'too_dense', 'too_sparse')
   */
  getZoomRecommendation(dataPoints) {
    if (dataPoints < 10) return 'too_sparse';
    if (dataPoints > 1000) return 'too_dense';
    return 'optimal';
  }
}

export default ZoomController;